/**
 * Avatar — companion/user image with a graceful fallback. When `src` is absent
 * or fails to load, renders initials derived from `name`. Token-driven, sized
 * and shaped via props. `alt` is required (a11y, no bare literal).
 */
import type { JSX } from 'solid-js';
import { createSignal, Show } from 'solid-js';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';
export type AvatarShape = 'circle' | 'rounded';

const SIZE_VAR: Record<AvatarSize, string> = {
  sm: 'var(--space-6)',
  md: 'var(--space-8)',
  lg: 'var(--space-12)',
  xl: 'var(--space-16)',
};

const STYLE_ID = 'f4-avatar';
const CSS = `
.f4-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  user-select: none;
}
.f4-avatar--circle { border-radius: var(--radius-circle); }
.f4-avatar--rounded { border-radius: var(--radius-md); }
.f4-avatar__img { width: 100%; height: 100%; object-fit: cover; }
`;

export interface AvatarProps {
  /** Image URL. When missing or broken, initials render instead. */
  readonly src?: string;
  /** Accessible name / image alt text (required). */
  readonly alt: string;
  /** Full name used to derive fallback initials. Defaults to `alt`. */
  readonly name?: string;
  /** Size preset. Defaults to `md`. */
  readonly size?: AvatarSize;
  /** Shape. Defaults to `circle`. */
  readonly shape?: AvatarShape;
  readonly class?: string;
}

/** First letters of up to the first two words, uppercased. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return words.map((w) => w.charAt(0).toUpperCase()).join('') || '?';
}

export function Avatar(props: AvatarProps): JSX.Element {
  ensureStyles(STYLE_ID, CSS);
  const [failed, setFailed] = createSignal(false);
  const dimension = (): string => SIZE_VAR[props.size ?? 'md'];
  const showImage = (): boolean => Boolean(props.src) && !failed();

  return (
    <span
      class={cx('f4-avatar', `f4-avatar--${props.shape ?? 'circle'}`, props.class)}
      style={{ width: dimension(), height: dimension() }}
    >
      <Show
        when={showImage()}
        fallback={
          <span aria-label={props.alt} role="img">
            {initials(props.name ?? props.alt)}
          </span>
        }
      >
        <img
          class="f4-avatar__img"
          src={props.src}
          alt={props.alt}
          onError={() => setFailed(true)}
        />
      </Show>
    </span>
  );
}
